import React, { useEffect, useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Search, MapPin, Briefcase, Building2, Filter, Loader2, Sparkles, Check } from 'lucide-react';
import JobCard from '../components/JobCard';
import CompanyLogoCarousel from '../components/CompanyLogoCarousel';
import api from '../api';
import { calculateMatchScore } from '../utils/scoringUtils';
import { parseJobDescription } from '../utils/descriptionParser';

export default function Home() {
  const [jobs, setJobs] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);

  // Filters
  const [title, setTitle] = useState('');
  const [location, setLocation] = useState('');
  const [experience, setExperience] = useState('');
  const [companyFilter, setCompanyFilter] = useState('');
  const [selectedCompany, setSelectedCompany] = useState('');

  // High Match Filter
  const [showHighMatchOnly, setShowHighMatchOnly] = useState(false);

  // Get user for scoring
  const user = useMemo(() => {
    try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch (e) { return {}; }
  }, []);

  const limit = 20;

  const fetchJobs = async (reset = false) => {
    setLoading(true);
    try {
      const res = await api.get('/jobs', {
        params: {
          title,
          location,
          company: companyFilter,
          experience,
          page: reset ? 1 : page,
          limit,
        },
      });

      const { jobs: newJobs, hasMore: more } = res.data;

      if (reset) {
        setJobs(newJobs);
        setPage(2);
      } else {
        setJobs((prev) => [...prev, ...newJobs]);
        setPage((prev) => prev + 1);
      }
      setHasMore(more);
    } catch (err) {
      console.error('Error fetching jobs:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setPage(1);
    fetchJobs(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, location, experience, companyFilter]);

  const handleLoadMore = () => {
    if (!loading && hasMore) {
      fetchJobs(false);
    }
  };

  const handleCompanySelect = (companyName) => {
    if (companyName === selectedCompany) {
      // Clicking same company clears filter
      setCompanyFilter('');
      setSelectedCompany('');
    } else {
      setCompanyFilter(companyName);
      setSelectedCompany(companyName);
    }
  };

  // Filter and Sort Jobs based on Match Score
  const displayedJobs = useMemo(() => {
    if (!showHighMatchOnly) return jobs;

    return jobs.map(job => {
      // We parse here transiently for sorting; JobCard will re-parse but that's fine for now
      const parsed = parseJobDescription(job.description);
      const match = calculateMatchScore(parsed, user);
      return { ...job, _tempScore: match.score };
    })
      .filter(j => j._tempScore >= 70)
      .sort((a, b) => b._tempScore - a._tempScore);
  }, [jobs, showHighMatchOnly, user]);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-indigo-600 via-violet-600 to-purple-700 dark:from-slate-900 dark:via-purple-900 dark:to-slate-900 text-white overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS1vcGFjaXR5PSIwLjA1IiBzdHJva2Utd2lkdGg9IjEiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZ3JpZCkiLz48L3N2Zz4=')] opacity-30"></div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
              Find Your Dream Job <br className="hidden sm:block" />
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-cyan-200 to-blue-100">
                in One Place
              </span>
            </h1>
            <p className="text-lg md:text-xl text-indigo-100 mb-10 max-w-2xl mx-auto">
              Discover thousands of opportunities from top companies. Start your career journey today.
            </p>

            {/* Search Bar */}
            <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl p-3 max-w-5xl mx-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                <div className="relative">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="text"
                    placeholder="Job title or keyword"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    className="w-full pl-12 pr-4 py-3.5 bg-slate-50 border-0 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div className="relative">
                  <Building2 className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="text"
                    placeholder="Company name"
                    value={companyFilter}
                    onChange={(e) => {
                      setCompanyFilter(e.target.value);
                      setSelectedCompany(e.target.value);
                    }}
                    className="w-full pl-12 pr-4 py-3.5 bg-slate-50 border-0 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div className="relative">
                  <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="text"
                    placeholder="Location"
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    className="w-full pl-12 pr-4 py-3.5 bg-slate-50 border-0 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div className="relative">
                  <Briefcase className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="text"
                    placeholder="Experience level"
                    value={experience}
                    onChange={(e) => setExperience(e.target.value)}
                    className="w-full pl-12 pr-4 py-3.5 bg-slate-50 border-0 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>
            </div>

            <div className="mt-6 flex items-center justify-center gap-8 text-sm text-indigo-100">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-cyan-400 rounded-full animate-pulse"></div>
                <span>{jobs.length}+ Jobs Available</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Company Carousel */}
      <CompanyLogoCarousel
        onSelect={handleCompanySelect}
        selectedCompany={selectedCompany}
      />

      {/* Selected Company Banner */}
      {selectedCompany && (
        <div className="bg-indigo-50 dark:bg-indigo-900/20 border-b border-indigo-100 dark:border-indigo-800/30">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
            <div className="flex items-center justify-between">
              <p className="text-sm text-slate-700 dark:text-slate-300">
                Showing jobs for: <span className="font-semibold text-indigo-600 dark:text-indigo-400">{selectedCompany}</span>
              </p>
              <button
                onClick={() => {
                  setCompanyFilter('');
                  setSelectedCompany('');
                }}
                className="text-sm text-slate-600 dark:text-slate-400 hover:text-indigo-600 dark:hover:text-indigo-400 font-medium"
              >
                Clear Filter
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Jobs Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 dark:text-slate-100">
              {showHighMatchOnly ? 'Top Matched Jobs' : (selectedCompany ? `Jobs at ${selectedCompany}` : 'Latest Opportunities')}
            </h2>
            <p className="text-slate-500 dark:text-slate-400 mt-1">
              {loading ? 'Loading...' : `${displayedJobs.length} positions found`}
            </p>
          </div>

          <div className="flex gap-3">
            <button
              onClick={() => setShowHighMatchOnly(!showHighMatchOnly)}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl border transition-all ${showHighMatchOnly ? 'bg-indigo-600 border-indigo-600 text-white shadow-lg shadow-indigo-500/20' : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:border-indigo-300'}`}
            >
              <Sparkles className={`w-4 h-4 ${showHighMatchOnly ? 'text-white' : 'text-indigo-500'}`} />
              <span className="font-medium text-sm">High Match Only</span>
              {showHighMatchOnly && <Check className="w-4 h-4 ml-1" />}
            </button>

            <button className="btn-ghost flex items-center gap-2">
              <Filter className="w-4 h-4" />
              <span className="hidden sm:inline">Filters</span>
            </button>
          </div>
        </div>

        {/* Job Grid */}
        {loading && jobs.length === 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="card p-6 animate-pulse">
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 bg-slate-200 rounded-lg"></div>
                  <div className="w-16 h-5 bg-slate-200 rounded-full"></div>
                </div>
                <div className="space-y-3">
                  <div className="h-5 bg-slate-200 rounded w-3/4"></div>
                  <div className="h-4 bg-slate-200 rounded w-1/2"></div>
                  <div className="flex gap-2 mt-4">
                    <div className="h-6 bg-slate-200 rounded-full w-20"></div>
                    <div className="h-6 bg-slate-200 rounded-full w-24"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : displayedJobs.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {displayedJobs.map((job, idx) => (
              <JobCard key={job.id ?? idx} job={job} />
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-slate-100 dark:bg-slate-800 mb-4">
              <Sparkles className="w-8 h-8 text-indigo-400" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2">No matching jobs found</h3>
            <p className="text-slate-500 dark:text-slate-400">
              {showHighMatchOnly ? "Try updating your profile resume or clearing the filter." : "Try adjusting your search criteria"}
            </p>
            {showHighMatchOnly && (
              <button onClick={() => setShowHighMatchOnly(false)} className="mt-4 text-indigo-600 hover:underline">
                View all jobs
              </button>
            )}
          </div>
        )}

        {/* Load More */}
        {hasMore && !showHighMatchOnly && (
          <div className="mt-12 text-center">
            <button
              onClick={handleLoadMore}
              disabled={loading}
              className="btn-primary px-8 py-3 inline-flex items-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Loading...
                </>
              ) : (
                'Load More Jobs'
              )}
            </button>
          </div>
        )}

        {!hasMore && jobs.length > 0 && (
          <p className="text-center text-slate-500 dark:text-slate-400 mt-12 pb-8">
            You've reached the end of the list
          </p>
        )}
      </section>
    </div>
  );
}
